import { BrowserModule } from '@angular/platform-browser';
import { HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, LOCALE_ID, CUSTOM_ELEMENTS_SCHEMA, Injector } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { FeatureFlagService } from './cp/shared/services/feature-flag.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { NgxUiLoaderModule, NgxUiLoaderConfig, SPINNER, POSITION, PB_DIRECTION } from 'ngx-ui-loader';
import { DashboardComponent } from './cp/dashboard/dashboard.component';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { ChartsModule } from '@progress/kendo-angular-charts';
import 'hammerjs';
import { DateInputsModule, DatePickerModule } from '@progress/kendo-angular-dateinputs';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { ExcelExportModule } from '@progress/kendo-angular-excel-export';
import { GaugesModule } from '@progress/kendo-angular-gauges';
import { GridModule, PDFModule, ExcelModule } from '@progress/kendo-angular-grid';
import { InputsModule, TextBoxModule } from '@progress/kendo-angular-inputs';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { TreeViewModule } from '@progress/kendo-angular-treeview';
import { LabelModule } from '@progress/kendo-angular-label';
import { MenuModule, MenusModule } from '@progress/kendo-angular-menu';
import { NotificationModule } from '@progress/kendo-angular-notification';
import { PDFExportModule } from '@progress/kendo-angular-pdf-export';
import { PopupModule } from '@progress/kendo-angular-popup';
import { SchedulerModule } from '@progress/kendo-angular-scheduler';
import { ScrollViewModule } from '@progress/kendo-angular-scrollview';
import { ToolBarModule } from '@progress/kendo-angular-toolbar';
import { TooltipsModule, PopoverModule } from '@progress/kendo-angular-tooltip';
import { UploadModule } from '@progress/kendo-angular-upload';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { GlobalsService } from './cp/shared/services/globals.service';
import { DispatchesService } from './cp/shared/services/dispatches.service';
import { DispatchAssignmentsComponent } from './cp/dispatches/assignments/dispatch-assignments.component';
import { WorkOrderAttachmentsComponent } from './cp/dispatches/attachments/workorder-attachments.component';
import { DispatchEquipSvcdComponent } from './cp/dispatches/equipment/serviced/dispatch-equip-svcd.component';
import { DispatchHistoryComponent } from './cp/dispatches/history/dispatch-history.component';
import { DispatchStatusHistoryComponent } from './cp/dispatches/history/status/dispatch-status-history.component';
import { DispatchLaborComponent } from './cp/dispatches/labor/dispatch-labor.component';
import { WorkOrderNotesComponent } from './cp/dispatches/notes/workorder-notes.component';
import { DispatchPartsOrderedComponent } from './cp/dispatches/parts/ordered/dispatch-parts-ordered.component';
import { DispatchPartsRequestedComponent } from './cp/dispatches/parts/requested/dispatch-parts-requested.component';
import { DispatchPartsComponent } from './cp/dispatches/parts/used/dispatch-parts.component';
import { WorkOrderPhotosComponent } from './cp/dispatches/photos/workorder-photos.component';
import { DispatchesComponent } from './cp/dispatches/dispatches.component';
import { DispatchSummaryComponent } from './cp/dispatches/dispatch-summary/dispatch-summary.component';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { DispatchStatusComponent } from './cp/dispatches/dispatch-status/dispatch-status.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MainLayoutComponent } from '@app/core/layout/app-layouts/main-layout.component';
import { AuthLayoutComponent } from '@app/core/layout/app-layouts/auth-layout.component';
import { DispatchAttachmentsComponent } from './cp/dispatches/dispatch-attachments/dispatch-attachments.component';
import { DispatchPhotosComponent } from './cp/dispatches/dispatch-photos/dispatch-photos.component';
import { DispatchNotesComponent } from './cp/dispatches/dispatch-notes/dispatch-notes.component';
import { DispatchHistorySearchComponent } from './cp/dispatch-history/dispatch-history-search.component';
import { DispatchSearchComponent } from './cp/dispatch-history/dispatch-search/dispatch-search.component';
import { CustomerService } from './cp/shared/services/customer.service';
import { EmployeeService } from './cp/shared/services/employee.service';
import { DateRangeFilterComponent } from './cp/shared/grid-filters/date-range-filter.component';
import { GridSearchComponent } from './cp/shared/grid-filters/grid-search/grid-search.component';
import { EmployeeComponent } from './cp/employees/employee.component';
import { EmployeeTrainingComponent } from './cp/employees/training/employee-training.component';
import { EmployeeDocumentsComponent } from './cp/employees/documents/employee-documents.component';
import { EmpTimeoffComponent } from './cp/employees/emp-timeoff/emp-timeoff.component';
import { EmpTimeoffEditComponent } from './cp/employees/emp-timeoff/emp-timeoff-edit/emp-timeoff-edit.component';
import { UnderConstructionComponent } from './cp/shared/under-construction/under-construction.component';
import { EOSService } from './cp/shared/services/eos.service';
import { DQOIComponent } from './cp/dqoi/dqoi.component';
import { QuotesComponent } from './cp/quotes/quotes.component';
import { OrdersComponent } from './cp/orders/orders.component';
import { InvoicesComponent } from './cp/invoices/invoices.component';
import { DQOISearchComponent } from './cp/dqoi/search/dqoi-search.component';
import { DQOIService } from './cp/shared/services/DQOI.service';
import { DetailSearchComponent } from './cp/dqoi/search/detail-search/detail-search.component';
import { DispatchSearchGridComponent } from './cp/dqoi/search/detail-search/dispatch-search-grid/dispatch-search-grid.component';
import { InvoiceSearchGridComponent } from './cp/dqoi/search/detail-search/invoice-search-grid/invoice-search-grid.component';
import { DQOIQuickSearchComponent } from './cp/shared/search/dqoi-search/dqoi-quick-search.component';
import { DispatchSearchResultsComponent } from './cp/shared/search/dqoi-search/dispatch-search-results/dispatch-search-results.component';
import { QuoteSearchResultsComponent } from './cp/shared/search/dqoi-search/quote-search-results/quote-search-results.component';
import { OrderSearchResultsComponent } from './cp/shared/search/dqoi-search/order-search-results/order-search-results.component';
import { BaseDQOIComponent } from './cp/shared/base-components/base-dqoi.component';
import { QuoteService } from './cp/shared/services/quote.service';
import { InvoiceService } from './cp/shared/services/invoice.service';
import { InvoiceSearchResultsComponent } from './cp/shared/search/dqoi-search/invoice-search-results/invoice-search-results.component';
import { CurrencyPipe } from '@angular/common';
import { DatePipe } from '@progress/kendo-angular-intl';
import { AddressBlockComponent } from './cp/shared/contact/address-block/address-block.component';
import { InvoiceLineItemsComponent } from './cp/invoices/invoice-lineitems/invoice-lineitems.component';
import { InvoiceNotesComponent } from './cp/invoices/invoice-notes/invoice-notes.component';
import { InvoiceAttachmentsComponent } from './cp/invoices/attachments/invoice-attachments.component';
import { InvoiceEquipmentComponent } from './cp/invoices/equipment/invoice-equipment.component';
import { QuoteEquipmentComponent } from './cp/quotes/equipment/quote-equipment.component';
import { QuoteAttachmentsComponent } from './cp/quotes/attachments/quote-attachments.component';
import { EditorModule } from '@progress/kendo-angular-editor';
import { OrderService } from './cp/shared/services/order.service';
import { QuoteSearchGridComponent } from './cp/dqoi/search/detail-search/quote-search-grid/quote-search-grid.component';
import { OrderSearchGridComponent } from './cp/dqoi/search/detail-search/order-search-grid/order-search-grid.component';
import { PopupAnchorDirective } from './cp/shared/directives/popup.anchor-target.directive';
import { ChartWidgetComponent } from './cp/shared/charts/chart-widget/chart-widget.component';
import { ChartBaseWidgetComponent } from './cp/shared/charts/chart-base-widget/chart-base-widget.component';
import { WidgetSearchComponent } from './cp/shared/search/widget-search/widget-search.component';
import { DashboardService } from './cp/shared/services/dashboard.service';
import { DispatchGridComponent } from './cp/shared/charts/chart-details/dispatch/dispatch-grid.component';
import { DashboardWidgetService } from './cp/shared/services/dashboardWidget.service';
import { InventorySelectorComponent } from './cp/shared/search/inventory-selector/inventory-selector.component';
import { InventoryService } from './cp/shared/services/inventory.service';
import { ClockInOutComponent } from './cp/employees/clock-in-out/clock-in-out.component';
import { QuoteLineItemsComponent } from './cp/quotes/quote-lineitems/quote-lineitems.component';
import { SearchService } from './cp/shared/services/search.service';
import { QuoteNotesComponent } from './cp/quotes/quote-notes/quote-notes.component';
import { AddNoteComponent } from './cp/shared/components/add-note/add-note.component';
import { GeographyInfoComponent } from './cp/shared/contact/geography-info/geography-info.component';
import { MediaService } from './cp/shared/services/media.service';
import { DisplayNotesComponent } from './cp/shared/components/display-notes/display-notes.component';
import { OpenDispatchesComponent } from './cp/dashboard/widgets/open-dispatches/open-dispatches.component';
import { OpenContractsComponent } from './cp/dashboard/widgets/open-contracts/open-contracts.component';
import { PendingChangeOrdersComponent } from './cp/dashboard/widgets/pending-change-orders/pending-change-orders.component';
import { InvoiceApprovalsComponent } from './cp/dashboard/widgets/invoice-approvals/invoice-approvals.component';
import { InvoiceChecklistComponent } from './cp/invoices/invoice-checklist/invoice-checklist.component';
import { ConstructionJobsComponent } from './cp/construction/jobs/construction-jobs.component';
import { JobsAssignmentHistoryComponent } from './cp/construction/jobs/assignment-history/jobs-assignment-history.component';
import { JobsAssetsComponent } from './cp/construction/jobs/assets/jobs-assets.component';
import { JobsEquipSvcdComponent } from './cp/construction/jobs/equipment/serviced/jobs-equip-svcd.component';
import { JobNotesComponent } from './cp/construction/jobs/notes/job-notes.component';
import { JobsLaborComponent } from './cp/construction/jobs/labor/jobs-labor.component';
import { JobAttachmentsComponent } from './cp/construction/jobs/attachments/job-attachments.component';
import { JobPartsOrderedComponent } from './cp/construction/jobs/parts/ordered/job-parts-ordered.component';
import { JobPartsRequestedComponent } from './cp/construction/jobs/parts/requested/job-parts-requested.component';
import { JobPartsUsedComponent } from './cp/construction/jobs/parts/used/job-parts-used.component';
import { JobSubPOComponent } from './cp/construction/jobs/ordered/job-sub-po.component';
import { ConstructionJobInformationComponent } from './cp/construction/Job-information/job-information.component';
import { OrderLineItemsComponent } from './cp/orders/order-lineitems/order-lineitems.component';
import { OrderErrorsComponent } from './cp/orders/order-errors/order-errors.component';
import { OrderAttachmentsComponent } from './cp/orders/order-attachments/order-attachments.component';
import { WarehouseTransferComponent } from './cp/inventory/warehouse-transfer/warehouse-transfer.component';
import { PartsComponent } from './cp/inventory/parts/parts.component';
import { PartInventoryComponent } from './cp/inventory/parts/inventory/part-inventory.component';
import { CycleCountingComponent } from './cp/inventory/cycle-counting/cycle-counting.component';
import { PartSuppliersComponent } from './cp/inventory/parts/suppliers/part-suppliers.component';
import { PartThresholdsComponent } from './cp/inventory/parts/thresholds/part-thresholds.component';
import { ZeroDollarOrdersComponent } from './cp/dashboard/widgets/zero-dollar-orders/zero-dollar-orders.component';
import { MonthlyInvoiceSalesDetailComponent } from './cp/dashboard/widgets/monthly-invoice-sales-detail/monthly-invoice-sales-detail.component';
import { QuoteSummaryComponent } from './cp/dashboard/widgets/quote-summary/quote-summary.component';
import { QuotesPendingApprovalComponent } from './cp/dashboard/widgets/quotes-pending-approval/quotes-pending-approval.component';
import { PurchaseOrderSummaryComponent } from './cp/dashboard/widgets/purchase-order-summary/purchase-order-summary.component';
import { OpenQuotesComponent } from './cp/dashboard/widgets/open-quotes/open-quotes.component';
import { TechHoursSummaryComponent } from './cp/dashboard/widgets/tech-hours-summary/tech-hours-summary.component';
import { ApplicationHelpComponent } from './cp/dashboard/widgets/application-help/application-help.component';
import { DispatchEquipmentHistoryComponent } from './cp/dashboard/widgets/dispatch-equipment-history/dispatch-equipment-history.component';
import { FieldgeticsNewsComponent } from './cp/dashboard/widgets/fieldgetics-news/fieldgetics-news.component';
import { OpenVendorReturnsComponent } from './cp/dashboard/widgets/open-vendor-returns/open-vendor-returns.component';
import { DispatchStatusGaugeComponent } from './cp/dashboard/widgets/dispatch-status-gauge/dispatch-status-gauge.component';
import { AccountReceivablesSummaryComponent } from './cp/dashboard/widgets/account-receivables-summary/account-receivables-summary.component';
import { InvoiceSalesSummaryComponent } from './cp/dashboard/widgets/invoice-sales-summary/invoice-sales-summary.component';
import { ShowCustomerStatisticsComponent } from './cp/dashboard/widgets/show-customer-statistics/show-customer-statistics.component';
import { TechStatSummaryComponent } from './cp/dashboard/widgets/tech-stat-summary/tech-stat-summary.component';
import { DQOIMainComponent } from './cp/dqoi/dqoi-main.component';
import { ConstructionService } from './cp/shared/services/construction.service';
import { ConstructionContractsComponent } from './cp/construction/contracts/construction-contracts.component';
import { ContractJobsComponent } from './cp/construction/contracts/jobs/contract-jobs.component';
import { ContractMfgWarrantyJobsComponent } from './cp/construction/contracts/mfg-warranty-jobs/contract-mfg-warranty-jobs.component';
import { ContractPostWarrantyJobsComponent } from './cp/construction/contracts/post-warranty-jobs/contract-post-warranty-jobs.component';
import { ContractChangeOrdersComponent } from './cp/construction/contracts/change-orders/contract-change-orders.component';
import { ContractVendorCreditsComponent } from './cp/construction/contracts/vendor-credits/contract-vendor-credits.component';
import { ConstructionContractInformationComponent } from './cp/construction/contract-information/contract-information.component';
import { ConstructionWIPContractBoardComponent } from './cp/construction/wip-contract-board/wip-contract-board.component';
import { OpenPOComponent } from './cp/inventory/open-po/open-po.component';
import { VendorReturnsComponent } from './cp/inventory/vendor-returns/vendor-returns.component';
import { VendorReturnPartsComponent } from './cp/inventory/vendor-returns/vendor-return-parts/vendor-return-parts.component';
import { InventoryLocationsComponent } from './cp/inventory/parts/inventory-locations/inventory-locations.component';
import { DispatchCustomerSuppliedPartsComponent } from './cp/dispatches/parts/customer-supplied/dispatch-customer-supplied-parts.component';
import { SupplierInfoComponent } from './cp/shared/contact/supplier-info/supplier-info.component';
import { DispatchAssignmentHistoryComponent } from './cp/dispatches/assignment-history/dispatch-assignment-history.component';
import { PurchaseOrdersComponent } from './cp/inventory/purchase-orders/purchase-orders.component';
import { DispatchBoardComponent } from './cp/dispatch-board/dispatch-board.component';
import { DispatchBoardCalendarComponent } from './cp/dispatch-board-calendar/dispatch-board-calendar.component';
import { DispatchBoardRibbonComponent } from './cp/dispatch-board/dispatch-board-ribbon/dispatch-board-ribbon.component';
import { StatusPanelComponent } from './cp/dispatch-board/status-panel/status-panel.component';
import { TechPanelComponent } from './cp/dispatch-board/tech-panel/tech-panel.component';
import { DispatchBoardWidgetComponent } from './cp/dispatch-board/dispatch-board-widget/dispatch-board-widget.component';
import { PriorityComponent } from './cp/dispatch-board/dispatch-board-widget/dispatch-board-widget-status/priority/priority.component';
import { TechWeekComponent } from './cp/dispatch-board/dispatch-board-widget/dispatch-board-widget-status/tech-week/tech-week.component';
import { TechWeekTooltipComponent } from './cp/dispatch-board/dispatch-board-widget/dispatch-board-widget-status/tech-week/tech-week-tooltip/tech-week-tooltip.component';
import { PriorityTooltipComponent } from './cp/dispatch-board/dispatch-board-widget/dispatch-board-widget-status/priority/priority-tooltip/priority-tooltip.component';
import { StatusPanelDetailComponent } from './cp/dispatch-board/status-panel/status-panel-detail/status-panel-detail.component';
import { TechPanelDetailComponent } from './cp/dispatch-board/tech-panel/tech-panel-detail/tech-panel-detail.component';
import { DispatchObjComponent } from './cp/dispatch-board/dispatch-obj/dispatch-obj.component';
import { QuotesGridComponent } from './cp/shared/charts/chart-details/quotes-grid/quotes-grid.component';
import { WindowComponent } from './cp/shared/window/window.component';
import { AssignTechComponent } from './cp/dispatch-board/modal/assign-tech/assign-tech.component';
import { MoveDispatchComponent } from './cp/dispatch-board/modal/move-dispatch/move-dispatch.component';
import { DispatchErrorsComponent } from './cp/dispatches/errors/dispatch-errors.component';
import { EquipmentService } from './cp/shared/services/equipment.service';
import { EquipCustomFieldsComponent } from './cp/dispatches/equipment/equip-custom-fields/equip-custom-fields.component';
import { DispatchVendorCreditsComponent } from './cp/dispatches/vendor-credits/dispatch-vendor-credits.component';
import { DispatchAssetsComponent } from './cp/dispatches/assets/dispatch-assets.component';
import { DashboardRibbonComponent } from './cp/dashboard/dashboard-ribbon/dashboard-ribbon.component';
import { DispatchBoardService } from './cp/shared/services/dispatch-board.service'
import { QuoteTemplateSaveComponent } from './cp/quotes/quote-template-save/quote-template-save.component';
import { BaseEditComponent } from './cp/shared/base-components/base-edit.component';
import { ReceivingComponent } from './cp/orders/modal/receiving/receiving.component';
import { BillsComponent } from './cp/orders/modal/bills/bills.component';
import { VendorCreditsComponent } from './cp/orders/modal/vendor-credits/vendor-credits.component';
import { NewReceivingComponent } from './cp/orders/modal/new-receiving/new-receiving.component';
import { ImportBidComponent } from './cp/quotes/import-bid/import-bid.component';
import { ReceivingDetailComponent } from './cp/orders/modal/receiving/receiving-detail/receiving-detail.component';
import { BillDetailsComponent } from './cp/orders/modal/bills/bill-details/bill-details.component';
import { VendorCreditItemsComponent } from './cp/orders/modal/vendor-credits/vendor-credit-items/vendor-credit-items.component';
import { DispatchWidgetLoaderComponent } from './cp/dispatch-board/dispatch-widget-loader/dispatch-widget-loader.component';
import { DispatchObjLoaderComponent } from './cp/dispatch-board/dispatch-obj-loader/dispatch-obj-loader.component';
import { CustomDownloadModule } from './cp/shared/components/custom-downloads/custom.download.module';
import { OrderItemsSelectComponent } from './cp/orders/modal/vendor-credits/order-item-select/order-item-select.component';
import { BillDetailItemsComponent } from './cp/orders/modal/bills/bill-details/bill-detail-items/bill-detail-items.component';
import { BillPaymentsComponent } from './cp/orders/modal/bills/bill-details/bill-payments/bill-payments.component';
import { VendorReturnPartsComponentX } from './cp/inventory/vendor-returns/return-parts/return-parts.component';
import { GenericListComponent } from './cp/shared/components/generic-list/generic-list.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScheduleInvoicesComponent } from './cp/invoices/scheduled/schedule-invoices.component';
import { BaseGridEditAllLinesComponent } from './cp/shared/base-components/base-grid-edit-alllines.component';
import { OrderBillDetailsComponent } from './cp/orders/order-bills/order-bill-details/order-bill-details.component';
import { OrderBillsComponent } from './cp/orders/order-bills/order-bills.component';
import { DqoiPopupComponent } from './cp/dispatch-board/modal/dqoi-popup/dqoi-popup.component';
import { MessageInfoDialogComponent } from './cp/shared/components/message-info-dialog/message-info-dialog.component';
import { OrderBillDetailItemsComponent } from './cp/orders/order-bills/order-bill-details/bill-details/bill-detail-items/order-bill-detail-items.component';
import { OrderBillPaymentsComponent } from './cp/orders/order-bills/order-bill-details/bill-details/bill-payments/order-bill-payments.component';
import { WeekBoardComponent } from './cp/boards/week-board/week-board.component';
import { TimeOffCalComponent } from './cp/boards/time-off-cal/time-off-cal.component';
import { DispatchViewBuilderComponent } from './cp/dispatch-board/modal/dispatch-view-builder/dispatch-view-builder.component';
import { ChangeStatusDialogComponent } from './cp/dispatches/change-status/change-status-dialog.component';
import { BaseGridEditInvLinesComponent } from './cp/shared/base-components/base-grid-edit-invlines.component';
import { TechCalendarComponent } from './cp/dispatch-board/tech-panel/tech-panel-detail/tech-calendar/tech-calendar.component';
import { CalHourComponent } from './cp/dispatch-board/tech-panel/tech-panel-detail/tech-calendar/cal-hour/cal-hour.component';
import { ConstructionMainComponent } from './cp/construction/construction-main/construction-main.component';
import { ConstructionSearchComponent } from './cp/construction/construction-main/search/construction-search.component';
import { ConstructionQuickSearchComponent } from './cp/construction/construction-main/search/quick-search/construction-quick-search.component';
import { CBOIComponent } from './cp/construction/construction-main/cboi.component';
import { ContractHistorySearchComponent } from './cp/construction/contract-history/contract-history-search.component';
import { WidgetComponent } from './cp/dashboard/widgets/cp-widget/widget.component';
import { HeaderComponent } from './cp/dashboard/widgets/cp-widget/header/header.component';
import { BodyComponent } from './cp/dashboard/widgets/cp-widget/body/body.component';
import { PersonContactComponent } from './cp/shared/contact/person-contact/person-contact.component';
import { BaseAppEditComponent } from './cp/shared/base-components/base-app-edit.component';
import { BaseGridEditFormLinesComponent } from './cp/shared/base-components/base-grid-edit-formlines.component';
import { FocusDirective } from './cp/shared/directives/focus.directive';
import { OrdersGridComponent } from './cp/shared/charts/chart-details/orders/orders.component';
import { InvoicesGridComponent } from './cp/shared/charts/chart-details/invoices/invoices.component';
import { TechStatsComponent } from './cp/shared/charts/chart-details/tech-stats/tech-stats.component';
import { TechHoursComponent } from './cp/shared/charts/chart-details/tech-hours/tech-hours.component';
import { ZeroOrdersComponent } from './cp/shared/charts/chart-details/zero-orders/zero-orders.component';
import { EquipmentHistoryComponent } from './cp/shared/charts/chart-details/equipment-history/equipment-history.component';
import { StatusGaugeComponent } from './cp/shared/charts/chart-details/status-gauge/status-gauge.component';
import { ContractsComponent } from './cp/shared/charts/chart-details/contracts/contracts.component';
import { PendingChangeOrdersGridComponent } from './cp/shared/charts/chart-details/pending-change-orders/pending-change-orders.component';
import { PoSummaryComponent } from './cp/shared/charts/chart-details/po-summary/po-summary.component';
import { QuotesPendingComponent } from './cp/shared/charts/chart-details/quotes-pending/quotes-pending.component';
import { QuoteSummaryGridComponent } from './cp/shared/charts/chart-details/quote-summary/quote-summary-grid.component';
import { InvoiceSummaryComponent } from './cp/shared/charts/chart-details/invoice-summary/invoice-summary.component';
import { InvoiceDetailComponent } from './cp/shared/charts/chart-details/invoice-detail/invoice-detail.component';
import { ARSummaryComponent } from './cp/shared/charts/chart-details/ar-summary/ar-summary.component';
import { VendorReturnsGridComponent } from './cp/shared/charts/chart-details/vendor-returns/vendor-returns-grid.component';
import { ScheduleDispatchesComponent } from './cp/dispatches/scheduled/schedule-dispatches.component';
import { DQOIInWindowComponent } from './cp/shared/components/dqoi-popup/dqoi-in-window.component';
import { SubPOOrderInfoComponent } from './cp/construction/jobs/SubPOOrderInfo/subpo-orderinfo.component';
import { ProfitAnalysisComponent } from './cp/construction/profit-analysis/profit-analysis.component';
import { JobDrawsComponent } from './cp/construction/job-draws/job-draws.component';
import { JobSummaryComponent } from './cp/construction/job-summary/job-summary.component';
import { RefTrackingService } from './cp/shared/services/reftracking.service';
import { RefTrackAdminComponent } from './cp/refTracking/admin/reftrack-admin.component';
import { InventoryThresholdsComponent } from './cp/inventory/inventory-thresholds/inventory-thresholds.component';
import { StockPickupComponent } from './cp/inventory/stock-pickup/stock-pickup.component';
import { CommissionMaintenanceComponent } from './cp/payroll/commission-maintenance/commission-maintenance.component';
import { BlanketPOsComponent } from './cp/construction/jobs/blanket-pos/blanket-pos.component';
import { VehiclesStockMaintenanceComponent } from './cp/fleet-management/vehicles-stock-maintenance/vehicles-stock-maintenance.component';
import { MobilePreviewComponent } from './cp/refTracking/admin/mobile-preview/mobile-preview.component';

import { PdfViewerModule } from 'ng2-pdf-viewer';
import { AngularSignaturePadModule } from '@almothafar/angular-signature-pad';
import { LocationSelectorModule } from './cp/shared/search/location-selector/location-selector.module';
import { EventManagerComponent } from './cp/system-config/event-manager/event-manager.component';
import { PayrollService } from './cp/shared/services/payroll.service';
import { BaseAppMComponent } from './cp/shared/base-components/base-app-m.component';
import { QuickViewComponent } from './cp/quick-view/quick-view.component';
import { CPSearchModule } from './cp/shared/search/cp-search/cp-search.module';
import { InvoicePaymentsComponent } from './cp/invoices/payments/invoice-payments.component';
import { TaxCodeModule } from './cp/shared/components/tax-code/tax-code.module';
import { CanDeactivateGuard } from '@app/cp/shared/guards/deactivate.guard';
import { SharedComponentsModule } from './cp/shared/sharedComponents.module';
import { CollectionNotesComponent } from './cp/invoices/collection-notes/collection-notes.component';
import { Title } from '@angular/platform-browser';
import { BulkPaymentsComponent } from './cp/accounting/bulk-payments/bulk-payments.component';
import { ChartOfAccountsComponent } from './cp/accounting/chart-of-accounts/chart-of-accounts.component';
import { AccountClassesComponent } from './cp/accounting/account-classes/account-classes.component';
import { EmployHistoryComponent } from './cp/employees/employ-history/employ-history.component';
import { InventoryConsumablesComponent } from './cp/inventory/inventory-consumables/inventory-consumables.component';
import { InventoryConsMatchesComponent } from './cp/inventory/inventory-cons-matches/inventory-cons-matches.component';
import { DispatchRefrigQVComponent } from './cp/dispatch-refrig-qv/dispatch-refrig-qv.component';
import { DispatchQVComponent } from './cp/dispatch-qv/dispatch-qv.component';
import { DispatchRefrigQVWindowComponent } from './cp/dispatch-refrig-qv/qv-window/dispatch-refrig-qv-window.component';
import { DispatchQVWindowComponent } from './cp/dispatch-qv/dispatch-qv-window/dispatch-qv-window.component';
import { DispatchQVPartsComponent } from './cp/dispatch-qv/dispatch-qv-parts/dispatch-qv-parts.component';
import { DispatchQVPartsRequestedComponent } from './cp/dispatch-qv/dispatch-qv-parts-requested/dispatch-qv-parts-requested.component';
import { DispatchQVLaborComponent } from './cp/dispatch-qv/dispatch-qv-labor/dispatch-qv-labor.component';
import { DispatchKudosComponent } from './cp/dispatches/kudos/dispatch-kudos.component';
import { DispatchReviewLiteComponent } from './cp/dashboard/widgets/dispatch-review-lite/dispatch-review-lite.component';
import { DispatchReviewGridComponent } from './cp/shared/charts/chart-details/dispatch-review-grid/dispatch-review-grid.component';
import { DispatchChangeStatusDialogComponent } from './cp/dispatch-qv/dispatch-change-status-dialog/dispatch-change-status-dialog.component';
import { DispatchChangeAssignmentDialogComponent } from './cp/dispatch-qv/dispatch-change-assignment-dialog/dispatch-change-assignment-dialog.component';
import { KudosAndOpportunityComponent } from './cp/dispatches/kudos-opportunity/kudos-opportunity.component';
import { CallbackPersonComponent } from './cp/dispatches/callback-person/callback-person.component';
import { DispatchAddPartDialogComponent } from './cp/dispatch-qv/dispatch-add-part-dialog/dispatch-add-part-dialog.component';
import { DepartmentsDialogComponent } from './cp/employees/domain-data/departments-dialog/departments-dialog.component';
import { JobRolesDialogComponent } from './cp/employees/domain-data/job-roles-dialog/job-roles-dialog.component';
import { JobTitlesDialogComponent } from './cp/employees/domain-data/job-titles-dialog/job-titles-dialog.component';
import { SecJobRolesDialogComponent } from './cp/employees/domain-data/sec-job-roles-dialog/sec-job-roles-dialog.component';
import { CostCenterDialogComponent } from './cp/employees/domain-data/cost-center-dialog/cost-center-dialog.component';
import { UnionsDialogComponent } from './cp/employees/domain-data/unions-dialog/unions-dialog.component';
import { UnionXrefsComponent } from './cp/employees/domain-data/unions-dialog/union-xref/union-xref.component';
import { EmpNoteModule } from './cp/shared/components/emp-note/emp-note.module';
import { DispatchQvLaborAddDialogComponent } from './cp/dispatch-qv/dispatch-qv-labor-add-dialog/dispatch-qv-labor-add-dialog.component';
import { DispatchThirdPartyHistoryDialogComponent } from './cp/dispatches/dispatch-third-party-history-dialog/dispatch-third-party-history-dialog.component';
import { DispatchReftrackComponent } from './cp/dashboard/widgets/dispatch-reftrack/dispatch-reftrack.component';
import { DispatchReftrackDetailsComponent } from './cp/shared/charts/chart-details/dispatch-reftrack-details/dispatch-reftrack-details.component';
import { InvoiceEstimateDialogComponent } from './cp/invoices/invoice-estimate/invoice-estimate-dialog.component';
import { MobileLandingComponent } from './cp/system-config/mobile-landing/mobile-landing.component';
import { MobileReleaseVersionsComponent } from './cp/system-config/mobile-release-versions/mobile-release-versions.component';
import { DispatchCostAdjustmentsComponent } from './cp/construction/jobs/dispatch-cost-adjustments/dispatch-cost-adjustments.component';
import { MergeDispatchesComponent } from './cp/dispatches/merge-dispatches/merge-dispatches.component';
import { PmBoardComponent } from './cp/construction/pm-board/pm-board.component';
import { StatusPanelCalendarComponent } from './cp/dispatch-board-calendar/status-panel-calendar/status-panel-calendar.component';
import { StatusPanelDetailCalendarComponent } from './cp/dispatch-board-calendar/status-panel-calendar/status-panel-calendar-detail/status-panel-detail-calendar.component';
import { DispatchBoardWidgetCalendarComponent } from './cp/dispatch-board-calendar/dispatch-board-widget-calendar/dispatch-board-widget-calendar.component';
import { DispatchCalendarObjComponent } from './cp/dispatch-board-calendar/dispatch-calendar-obj/dispatch-calendar-obj.component';
import { DispatchQVDispatchBoardComponent } from './cp/dispatch-board-calendar/dispatch-qv-dispatch-board/dispatch-qv-dispatch-board.component';
import { DispatchObjLoaderCalendarComponent } from './cp/dispatch-board-calendar/dispatch-obj-loader-calendar/dispatch-obj-loader-calendar.component';
import { TechPanelComponentCalendarComponent } from './cp/dispatch-board-calendar/tech-panel-calendar/tech-panel-calendar.component';
import { DispatchBoardWidgetTechComponent } from './cp/dispatch-board-calendar/dispatch-board-widget-tech/dispatch-board-widget-tech.component';
import { TechPanelDetailCalendarComponent } from './cp/dispatch-board-calendar/tech-panel-calendar/tech-panel-detail/tech-panel-detail-calendar.component';
import { SeattleComponent } from './cp/system-config/mobile-landing/seattle/seattle.component';
import { InvoicePickerComponent } from './cp/dispatches/invoice-picker/invoice-picker.component';
import { QuotePickerComponent } from './cp/invoices/quote-picker/quote-picker.component';
import { WipReportComponent } from './cp/construction/wip-report/wip-report.component';
import { PartBranchThresholdsComponent } from './cp/inventory/parts/branch-threshold/part-branch-thresholds.component';
import { PartAlternativesThresholdsComponent } from './cp/inventory/parts/alternatives-threshold/part-alternatives-thresholds.component';
import { DomainService } from './cp/shared/services/domain.service';
import { BaseDataComponent } from './cp/domain/basedata/basedata.component';
import { OrderSearchComponent } from './cp/order-search/order-search.component';
import { DymoEtcPrintModule } from './cp/shared/components/dymo-etc-print/dymo-etc-print.module';
import { DispatchBoardCalendarAllComponent } from './cp/dispatch-board-calendar/dispatch-board-all-calendar/dispatch-board-all-calendar.component';
import { TechPanelCalendarAllComponent } from './cp/dispatch-board-calendar/tech-panel-calendar-all/tech-panel-calendar-all.component';
import { DeleteAssignmentComponent } from './cp/dispatch-board/modal/delete-assignment/delete-assignment.component';
import { pipeModule } from '@app/cp/shared/pipes/pipe.module'
import { DisableControlDirective } from './cp/shared/directives/disableControl.directive';
import { DispatchAccountClassDialogComponent } from './cp/shared/components/dispatch-account-class-dialog/dispatch-account-class-dialog.component';
import { InventoryLocationDialogComponent } from './cp/shared/components/inventory-location-dialog/inventory-location-dialog.component';
import { InvoiceCopytoNewComponent } from './cp/invoices/invoice-copyto-new/invoice-copyto-new.component';
import { InvCopytoNewLineItemsComponent } from './cp/invoices/invoice-copyto-new/lineitems/invcopytonew-lineitems.component';
import { ReportsComponent } from './cp/reports/reports.component';
import { DispatchEquipPartsComponent } from './cp/dispatches/equipment/parts/dispatch-equip-parts.component';
import { OrderReviewLiteComponent } from './cp/dashboard/widgets/order-review-lite/order-review-lite.component';
import { OrderReviewGridComponent } from './cp/shared/charts/chart-details/order-review-grid/order-review-grid.component';
import { OrderQvComponent } from './cp/order-qv/order-qv.component';
import { PartNbrsComponent } from './cp/inventory/parts/part-nbrs/part-nbrs.component';
import { ContractWipBoardComponent } from './cp/construction/contract-wip-board/contract-wip-board.component';
import { DispatchContractAdjustmentsComponent } from './cp/construction/jobs/dispatch-contract-adjustments/dispatch-contract-adjustments.component';
import { DispatchCurrentCostAdjustmentsComponent } from './cp/construction/jobs/dispatch-current-cost-adjustments/dispatch-current-cost-adjustments.component';
import { DispatchBtdAdjustmentsComponent } from './cp/construction/jobs/dispatch-btd-adjustments/dispatch-btd-adjustments.component';
import { DispatchSixzerosixAdjustmentsComponent } from './cp/construction/jobs/dispatch-sixzerosix-adjustments/dispatch-sixzerosix-adjustments.component';
import { DispatchMultiAdjustmentsComponent } from './cp/construction/jobs/dispatch-multi-adjustments/dispatch-multi-adjustments.component';
import { UserSettingDialogComponent } from './cp/employees/user-setting/user-setting.component';
import { ActiveInvoiceDisputeComponent } from './cp/dashboard/widgets/active-invoice-dispute/active-invoice-dispute.component';
import { ActiveInvoiceDisputesGridComponent } from './cp/shared/charts/chart-details/active-invoice-disputes/active-invoice-disputes.component';
import { CreditMemoApprovalComponent } from './cp/dashboard/widgets/credit-memo-approval/credit-memo-approval.component';
import { CreditMemoApprovalsGridComponent } from './cp/shared/charts/chart-details/credit-memo-approvals/credit-memo-approvals.component';
import { InvoiceDisputeCMComponent } from './cp/invoices/Invoice-dispute-cm/invoice-dispute-cm.component';
import { InvoiceCMCRComponent } from './cp/invoices/Invoice-cm-cr/invoice-cm-cr.component';
import { InvoiceCMCRCORRComponent } from './cp/invoices/Invoice-cm-crcorr/Invoice-cm-crcorr.component';
import { DispatchCompleteAdjustmentComponent } from './cp/construction/jobs/dispatch-complete-adjustment/dispatch-complete-adjustment.component';
import { CallbackRFBCheckComponent } from './cp/dispatches/callback-rfb-check/callback-rfb-check.component';
import { CreditMemoCORRComponent } from './cp/invoices/credit-memo-corr/credit-memo-corr.component';
import { CreditMemoCORRLineItemsComponent } from './cp/invoices/credit-memo-corr/lineitems/credit-memo-corr-lineitems.component';
import { RefTrackingComponent } from './cp/dispatches/ref-tracking/ref-tracking.component';
import { ExcelService } from './cp/shared/services/excel.service';
import { MonthEndDrawRecostComponent } from './cp/dashboard/widgets/month-end-draw-recost/month-end-draw-recost.component';
import { InvoicesReadyToPostGridComponent } from './cp/shared/charts/chart-details/invoices-ready-to-post/invoices-ready-to-post.component';
import { OpenPOSubComponent } from './cp/inventory/open-po-sub/open-po-sub.component';
import { ConstructionLandingComponent } from './cp/construction/construction-landing/construction-landing.component';
import { ProfitAnalysisLandingComponent } from './cp/construction/construction-landing/profit-analysis-landing/profit-analysis-landing.component';
import { SystemService } from './cp/shared/services/system.service';
import { OrderNotesComponent } from './cp/orders/notes/order-notes.component';
import { PartsHistorySearchComponent } from './cp/inventory/parts-history/parts-history-search.component';
import { PartsMergeSearchComponent } from './cp/inventory/parts-merge/parts-merge-search.component';
import { CopyFieldTextComponent } from "@app/cp/shared/components/copy-field-text/copy-field-text.component";
import { EquipmentBoardComponent } from './cp/construction/equipment-board/equipment-board.component';
import { DispatchEquipmentAdjustmentsComponent } from './cp/construction/jobs/dispatch-equipment-adjustments/dispatch-equipment-adjustments.component';
import { DispatchSowAdjustmentsComponent } from './cp/construction/jobs/dispatch-sow-adjustments/dispatch-sow-adjustments.component';
import { PartsCostHistoryComponent } from './cp/inventory/parts-cost-history/parts-cost-history.component';
import { PartsCostHistoryDetailComponent } from './cp/inventory/parts-cost-history/parts-cost-history-detail/parts-cost-history-detail.component';
import { PriceChangeBenefitComponent } from './cp/accounting/price-change-benefit/price-change-benefit.component';
import { DispatchCallbackComponent } from './cp/dispatches/dispatch-callback/dispatch-callback.component';
import { InvoiceActionComponent } from './cp/invoices/invoice-action/invoice-action.component';
import { CPStorageService } from './cp/shared/services/cpstorage.service';
import { IconsModule } from '@progress/kendo-angular-icons';
import { StockReplenishmentComponent } from './cp/inventory/parts/stock-replenishment/stock-replenishment.component';
import { StockReplenishmentDetailComponent } from './cp/inventory/parts/stock-replenishment-detail/stock-replenishment-detail.component';
import { InvoiceLineItemsHistoryComponent } from './cp/invoices/invoice-lineitems/invoice-lineitems-history/invoice-lineitems-history.component';
import { InvoiceAllHistoryComponent } from './cp/invoices/invoice-lineitems/invoice-all-history/invoice-all-history.component';
import { PotentialCustomersComponent } from './cp/construction/potential-customers/potential-customers.component';
import { PotentialLocationsComponent } from './cp/construction/potential-customers/potential-locations/potential-locations.component';
import { ZebraPrinterConfigComponent } from './cp/configuration/zebra-printer-config/zebra-printer-config.component';
import { DistributedPrintingService } from './cp/shared/services/distributed-printing.service';
import { CycleCountingHistoryComponent } from './cp/inventory/cycle-counting-history/cycle-counting-history.component';
import {
MsalGuard,
MsalInterceptor,
MsalBroadcastService,
MsalModule,
MsalService,
} from '@azure/msal-angular';
import * as msalfunctions from './core/msal-helpers';
import { LegacyTokenAuthorizationInterceptor } from './cp/shared/services/interceptors/legacy-token-authorization-interceptor';
import { NgxIndexedDBModule } from 'ngx-indexed-db';
import { dbConfig } from './cp/shared/database/db';
import { CachingInterceptor } from './cp/shared/services/interceptors/caching-interceptor';
import { MockUploadInterceptor } from './cp/shared/services/interceptors/mock-upload-interceptor';
import { PrintService } from './cp/shared/services/print.service';
import { environment } from '@env/environment';
import { AutoResizeTextareaDirective } from './cp/shared/directives/auto-resize-textarea.directive';
import { ErrorInterceptor } from './cp/shared/services/interceptors/error-interceptor';
import { HashTagSerializer } from './cp/shared/serializers/hash-tag-serializer';
import { UrlSerializer } from '@angular/router';
import { VersionCheckService } from './cp/shared/services/versioncheck.service';
import { ZuggitCpDispatchNeedsPartsComponent } from './cp/dashboard/widgets/zuggit-cp-dispatch-needs-parts/zuggit-cp-dispatch-needs-parts.component';
import { ZuggitCpDispatchNeedsPartsGridComponent } from './cp/shared/charts/chart-details/zuggit-cp-dispatch-needs-parts-grid/zuggit-cp-dispatch-needs-parts-grid.component';
import { AfterHoursPoComponent } from './cp/dispatches/after-hours-po/after-hours-po.component';
import { OpenPOBillsComponent } from './cp/inventory/open-po-bills/open-po-bills.component';
import { OpenPOVendorCreditsComponent } from './cp/inventory/open-po-vendor-credits/open-po-vendor-credits.component';
import { PriorityStatusPipe } from "@app/cp/shared/pipes/priority-status.pipe";

const ngxUiLoaderConfig: NgxUiLoaderConfig = {
  bgsColor: '#0720ef',
  bgsOpacity: 0.7,
  bgsPosition: POSITION.centerCenter,
  bgsSize: 100,
  bgsType: SPINNER.threeBounce,
  blur: 0,
  fgsColor: "#839395",
  fgsPosition: POSITION.centerCenter,
  fgsSize: 120,
  fgsType: SPINNER.threeBounce,
  gap: 24,
  overlayColor: "rgba(171,165,165,0.8)",
  pbColor: "#00ACC1",
  pbDirection: PB_DIRECTION.leftToRight, // progress bar direction
  pbThickness: 3,
  hasProgressBar: false,
  text: "Loading...",
  textColor: "black",
  textPosition: POSITION.centerCenter
};

Object.defineProperty(globalThis, 'enableDebugging', {
  get() {
    return environment.debug;
  },
  set(value) {
    console.warn('Debugging is now', value ? 'enabled' : 'disabled');
    environment.debug = value;
  },
  configurable: true
});

if (environment.production) {
  const originalLog = console.log;
  console.log = (message?: any, ...optionalParams: any[]) => {
    if (globalThis.enableDebugging) {
      originalLog(message, ...optionalParams);
    }
  }
  const originalDebug = console.debug;
  console.debug = (message?: any, ...optionalParams: any[]) => {
    if (globalThis.enableDebugging) {
      originalDebug(message, ...optionalParams);
    }
  };
}

@NgModule({ declarations: [
        AppComponent, DashboardComponent,
        DispatchAssignmentsComponent, WorkOrderAttachmentsComponent, DispatchEquipSvcdComponent, DispatchHistoryComponent, DispatchStatusHistoryComponent,
        DispatchLaborComponent, WorkOrderNotesComponent, OrderNotesComponent, DispatchPartsOrderedComponent, DispatchPartsRequestedComponent, DispatchPartsComponent,
        WorkOrderPhotosComponent, DispatchesComponent, DispatchSummaryComponent, DispatchStatusComponent, DispatchAttachmentsComponent, DispatchPhotosComponent,
        DispatchNotesComponent, DispatchHistorySearchComponent, DispatchSearchComponent, DateRangeFilterComponent, GridSearchComponent,
        EmployeeComponent, EmployeeTrainingComponent, EmployeeDocumentsComponent, EmpTimeoffComponent, EmpTimeoffEditComponent, UnderConstructionComponent, DQOIComponent,
        QuotesComponent, OrdersComponent, InvoicesComponent, InvoiceSearchResultsComponent, DQOISearchComponent, DispatchSearchGridComponent, DetailSearchComponent, InvoiceSearchGridComponent,
        DQOIQuickSearchComponent, DispatchSearchResultsComponent, QuoteSearchResultsComponent, OrderSearchResultsComponent, BaseDQOIComponent, AddressBlockComponent, InvoiceLineItemsComponent, InvoiceLineItemsHistoryComponent, InvoiceAllHistoryComponent,
        InvoiceNotesComponent, InvoiceAttachmentsComponent, InvoiceEquipmentComponent, QuotesComponent, QuoteEquipmentComponent, QuoteAttachmentsComponent,
        QuoteSearchGridComponent, OrderSearchGridComponent, ClockInOutComponent, PopupAnchorDirective, ChartWidgetComponent, WidgetSearchComponent, DispatchGridComponent,
        ChartBaseWidgetComponent, InventorySelectorComponent, QuoteLineItemsComponent,
        QuoteNotesComponent, AddNoteComponent, OpenDispatchesComponent, OpenContractsComponent, PendingChangeOrdersComponent,
        InvoiceApprovalsComponent, InvoiceChecklistComponent, ConstructionJobsComponent, JobsAssignmentHistoryComponent, JobsAssetsComponent,
        JobsEquipSvcdComponent, JobNotesComponent, JobsLaborComponent, JobAttachmentsComponent, JobPartsOrderedComponent, JobPartsRequestedComponent,
        JobPartsUsedComponent, JobSubPOComponent, ConstructionJobInformationComponent,
        GeographyInfoComponent, DisplayNotesComponent, InvoiceChecklistComponent, OrderLineItemsComponent, InvoicePaymentsComponent,
        OrderErrorsComponent, OrderAttachmentsComponent, AutoResizeTextareaDirective,
        WarehouseTransferComponent, StockReplenishmentComponent, PartsComponent, PartInventoryComponent, CycleCountingComponent, PartSuppliersComponent, PartBranchThresholdsComponent, PartAlternativesThresholdsComponent, PartNbrsComponent, PartThresholdsComponent, CycleCountingHistoryComponent, PartsHistorySearchComponent, PartsMergeSearchComponent,
        PartsCostHistoryComponent, PartsCostHistoryDetailComponent, StockReplenishmentDetailComponent,
        MonthEndDrawRecostComponent, InvoiceChecklistComponent, ZeroDollarOrdersComponent, MonthlyInvoiceSalesDetailComponent, QuoteSummaryComponent,
        QuotesPendingApprovalComponent, PurchaseOrderSummaryComponent, OpenQuotesComponent, TechHoursSummaryComponent, ApplicationHelpComponent,
        DispatchEquipmentHistoryComponent, FieldgeticsNewsComponent, OpenVendorReturnsComponent, DispatchStatusGaugeComponent,
        AccountReceivablesSummaryComponent, InvoiceSalesSummaryComponent, ShowCustomerStatisticsComponent, TechStatSummaryComponent,
        DQOIMainComponent, ConstructionContractsComponent, ConstructionLandingComponent, ContractJobsComponent, ContractMfgWarrantyJobsComponent, ContractPostWarrantyJobsComponent,
        ContractChangeOrdersComponent, ContractVendorCreditsComponent, ConstructionContractInformationComponent, ConstructionWIPContractBoardComponent,
        ContractChangeOrdersComponent, ContractVendorCreditsComponent, ConstructionContractInformationComponent, ConstructionWIPContractBoardComponent,
        OpenPOComponent, OpenPOVendorCreditsComponent, OpenPOBillsComponent, OpenPOSubComponent, VendorReturnsComponent, VendorReturnPartsComponent,
        InventoryLocationsComponent, DispatchCustomerSuppliedPartsComponent, SupplierInfoComponent,
        DispatchAssignmentHistoryComponent, PurchaseOrdersComponent, DispatchBoardComponent, StatusPanelComponent, TechPanelComponent,
        DispatchBoardWidgetComponent, PriorityComponent, TechWeekComponent, TechWeekTooltipComponent, PriorityTooltipComponent, StatusPanelDetailComponent,
        TechPanelDetailComponent, DispatchObjComponent, QuotesGridComponent, WindowComponent, AssignTechComponent, MoveDispatchComponent,
        DispatchAssignmentHistoryComponent, PurchaseOrdersComponent, DispatchErrorsComponent, EquipCustomFieldsComponent,
        DispatchVendorCreditsComponent, DispatchAssetsComponent, DispatchErrorsComponent, DashboardRibbonComponent, DispatchBoardRibbonComponent,
        QuoteTemplateSaveComponent, BaseEditComponent, ReceivingComponent, BillsComponent, VendorCreditsComponent, NewReceivingComponent,
        ImportBidComponent, ReceivingDetailComponent, BillDetailsComponent, VendorCreditItemsComponent,
        OrderItemsSelectComponent, BillDetailItemsComponent, BillPaymentsComponent, OrderBillDetailsComponent, OrderBillsComponent,
        OrderBillDetailItemsComponent, OrderBillPaymentsComponent, FocusDirective, ScheduleDispatchesComponent, DQOIInWindowComponent,
        DispatchWidgetLoaderComponent, DispatchObjLoaderComponent,
        VendorReturnPartsComponentX, GenericListComponent, ScheduleInvoicesComponent,
        BaseGridEditAllLinesComponent, MessageInfoDialogComponent, ChangeStatusDialogComponent,
        BaseGridEditInvLinesComponent, PersonContactComponent, BaseAppEditComponent, BaseGridEditFormLinesComponent, SubPOOrderInfoComponent, BlanketPOsComponent,
        ConstructionMainComponent, ConstructionSearchComponent, ConstructionQuickSearchComponent, ContractHistorySearchComponent,
        WeekBoardComponent,
        WeekBoardComponent,
        TimeOffCalComponent, DqoiPopupComponent, DispatchViewBuilderComponent, TechCalendarComponent, CalHourComponent, WidgetComponent,
        HeaderComponent, BodyComponent, OrdersGridComponent, InvoicesGridComponent, TechStatsComponent, TechHoursComponent, ZeroOrdersComponent, EquipmentHistoryComponent, InvoicesReadyToPostGridComponent,
        StatusGaugeComponent, ContractsComponent, PendingChangeOrdersGridComponent, PoSummaryComponent,
        QuotesPendingComponent, QuoteSummaryGridComponent, VendorReturnsGridComponent, ProfitAnalysisComponent, ProfitAnalysisLandingComponent, JobDrawsComponent, JobSummaryComponent, DepartmentsDialogComponent, UnionsDialogComponent,
        RefTrackAdminComponent, DispatchAddPartDialogComponent,
        UnionXrefsComponent, ActiveInvoiceDisputesGridComponent,
        CommissionMaintenanceComponent,
        VehiclesStockMaintenanceComponent,
        InventoryThresholdsComponent,
        StockPickupComponent,
        MobilePreviewComponent,
        StockPickupComponent,
        EventManagerComponent,
        BaseAppMComponent,
        QuickViewComponent,
        CollectionNotesComponent,
        CollectionNotesComponent,
        BulkPaymentsComponent,
        ChartOfAccountsComponent,
        AccountClassesComponent,
        DispatchQVComponent, DispatchQVWindowComponent, DispatchQVPartsComponent, DispatchQVPartsRequestedComponent, DispatchQVLaborComponent, DispatchKudosComponent, DispatchChangeStatusDialogComponent,
        EmployHistoryComponent, InventoryConsumablesComponent, InventoryConsMatchesComponent, DispatchReviewLiteComponent, DispatchReviewGridComponent, OrderReviewLiteComponent, OrderReviewGridComponent,
        KudosAndOpportunityComponent, DispatchChangeAssignmentDialogComponent,
        CallbackPersonComponent, JobRolesDialogComponent, JobTitlesDialogComponent, SecJobRolesDialogComponent, CostCenterDialogComponent, DispatchQvLaborAddDialogComponent,
        DispatchThirdPartyHistoryDialogComponent, OrderQvComponent,
        DispatchReftrackComponent, InvoicePickerComponent, QuotePickerComponent, ActiveInvoiceDisputeComponent,
        DispatchReftrackDetailsComponent,
        DispatchRefrigQVComponent, DispatchRefrigQVWindowComponent,
        InvoiceEstimateDialogComponent,
        MobileLandingComponent,
        MobileReleaseVersionsComponent,
        DispatchCostAdjustmentsComponent, TechPanelComponentCalendarComponent, DispatchBoardWidgetTechComponent, TechPanelDetailCalendarComponent,
        MergeDispatchesComponent,
        DispatchObjLoaderCalendarComponent,
        PmBoardComponent, DispatchBoardCalendarComponent, StatusPanelDetailCalendarComponent, StatusPanelCalendarComponent, DispatchBoardWidgetCalendarComponent, DispatchCalendarObjComponent, DispatchQVDispatchBoardComponent, SeattleComponent, WipReportComponent,
        BaseDataComponent, OrderSearchComponent, DeleteAssignmentComponent,
        DisableControlDirective, OrderSearchComponent, DeleteAssignmentComponent,
        BaseDataComponent, OrderSearchComponent, DispatchBoardCalendarAllComponent, TechPanelCalendarAllComponent,
        DispatchAccountClassDialogComponent,
        InventoryLocationDialogComponent, ReportsComponent, DispatchEquipPartsComponent,
        InvoiceCopytoNewComponent, InvCopytoNewLineItemsComponent, ContractWipBoardComponent, DispatchContractAdjustmentsComponent, DispatchCurrentCostAdjustmentsComponent, DispatchBtdAdjustmentsComponent, DispatchSixzerosixAdjustmentsComponent, DispatchMultiAdjustmentsComponent,
        UserSettingDialogComponent,
        CreditMemoApprovalComponent, CreditMemoApprovalsGridComponent,
        InvoiceDisputeCMComponent,
        InvoiceCMCRComponent,
        InvoiceCMCRCORRComponent, DispatchCompleteAdjustmentComponent,
        CallbackRFBCheckComponent, CreditMemoCORRComponent, CreditMemoCORRLineItemsComponent,
        RefTrackingComponent, CBOIComponent,
        CopyFieldTextComponent,
        EquipmentBoardComponent, DispatchEquipmentAdjustmentsComponent,
        DispatchSowAdjustmentsComponent, PriceChangeBenefitComponent,
        DispatchCallbackComponent, InvoiceActionComponent, PotentialCustomersComponent, PotentialLocationsComponent, ZebraPrinterConfigComponent, ZuggitCpDispatchNeedsPartsComponent, ZuggitCpDispatchNeedsPartsGridComponent, AfterHoursPoComponent
    ],
    exports: [EquipCustomFieldsComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [AppComponent], imports: [BrowserModule,
        HammerModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        MatAutocompleteModule,
        MatBadgeModule,
        MatBottomSheetModule,
        MatButtonModule,
        MatButtonToggleModule,
        MatCardModule,
        MatCheckboxModule,
        MatChipsModule,
        MatDatepickerModule,
        MatDialogModule,
        MatDividerModule,
        MatExpansionModule,
        MatGridListModule,
        MatIconModule,
        MatInputModule,
        MatListModule,
        MatMenuModule,
        MatNativeDateModule,
        MatPaginatorModule,
        MatProgressBarModule,
        MatProgressSpinnerModule,
        MatRadioModule,
        MatRippleModule,
        MatSelectModule,
        MatSidenavModule,
        MatSliderModule,
        MatSlideToggleModule,
        MatSnackBarModule,
        MatSortModule,
        MatStepperModule,
        MatTableModule,
        MatTabsModule,
        MatToolbarModule,
        MatTooltipModule,
        MatTreeModule,
        DragDropModule,
        AppRoutingModule,
        RouterModule,
        MainLayoutComponent,
        AuthLayoutComponent,
        EmpNoteModule,
        CoreModule,
        ButtonsModule,
        ChartsModule,
        DateInputsModule,
        DatePickerModule,
        DropDownsModule,
        ExcelExportModule,
        GaugesModule,
        DialogsModule,
        GridModule,
        MenusModule,
        PDFModule,
        ExcelModule,
        TextBoxModule,
        InputsModule,
        LayoutModule,
        TreeViewModule,
        LabelModule,
        MenuModule,
        NotificationModule,
        PDFExportModule,
        PopupModule,
        SchedulerModule,
        ScrollViewModule,
        ToolBarModule,
        TooltipsModule,
        PopoverModule,
        UploadModule,
        EditorModule,
        CustomDownloadModule,
        LocationSelectorModule,
        CPSearchModule,
        TaxCodeModule,
        SharedComponentsModule,
        DymoEtcPrintModule,
        NgxUiLoaderModule.forRoot(ngxUiLoaderConfig),
        PdfViewerModule, AngularSignaturePadModule,
        pipeModule,
        IconsModule,
        MsalModule.forRoot(msalfunctions.MSALInstanceFactory(), msalfunctions.MSALGuardConfigFactory(), msalfunctions.MSALInterceptorConfigFactory()),
        NgxIndexedDBModule.forRoot(dbConfig), PriorityStatusPipe], providers: [
        { provide: LOCALE_ID, useValue: 'en-US' },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        CPStorageService,
        CanDeactivateGuard,
        ConstructionService,
        CurrencyPipe,
        CustomerService,
        DQOIService,
        DashboardService,
        DashboardWidgetService,
        DatePipe,
        DispatchBoardService,
        DispatchesService,
        DistributedPrintingService,
        DomainService,
        EOSService,
        EmployeeService,
        EquipmentService,
        ExcelService,
        FeatureFlagService,
        PrintService,
        PrintService,
        GlobalsService,
        InventoryService,
        InvoiceService,
        MediaService,
        OrderService,
        PayrollService,
        QuoteService,
        RefTrackingService,
        SearchService,
        SystemService,
        VersionCheckService,
        Title,
        {
            provide: UrlSerializer,
            useClass: HashTagSerializer
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LegacyTokenAuthorizationInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: CachingInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MockUploadInterceptor,
            multi: true
        },
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule {
  static injector: Injector;

  constructor(injector: Injector) {
    AppModule.injector = injector;
  }
}
